/**
 * @fileoverview Two-Factor Authentication Form Component
 *
 * Renders the 2FA modal with choice between passkey and email code.
 */

import React from 'react';
import {
  FiArrowLeft,
  FiArrowRight,
  FiShield,
  FiMail
} from 'react-icons/fi';

/**
 * Two-Factor Choice Modal Component
 *
 * Allows user to choose between passkey and email code for 2FA.
 */
const TwoFactorChoiceModal = ({
  loading,
  hasBiometric,
  hasEmail,
  onBiometric2FA,
  onRequestEmailCode,
  onClose
}) => {
  return (
    <div className="lp-modal-overlay active">
      <div className="lp-modal">
        <div className="lp-modal-header">
          <button
            type="button"
            className="lp-modal-back"
            onClick={onClose}
            disabled={loading}
          >
            <FiArrowLeft size={18} />
          </button>
          <h2 className="lp-modal-title">Verify Your Identity</h2>
        </div>
        <p className="lp-modal-description">
          Choose how you want to verify your identity to continue signing in.
        </p>

        <div className="lp-auth-choice-list">
          {/* Passkey Option - only show if user has biometric */}
          {hasBiometric && (
            <button
              type="button"
              className="lp-auth-choice"
              onClick={onBiometric2FA}
              disabled={loading}
            >
              <div className="lp-auth-choice-icon passkey">
                <FiShield size={24} />
              </div>
              <div className="lp-auth-choice-content">
                <div className="lp-auth-choice-title">Use Passkey</div>
                <div className="lp-auth-choice-desc">Verify with Face ID, Touch ID, or your device PIN</div>
              </div>
              <div className="lp-auth-choice-badge">Recommended</div>
              <FiArrowRight className="lp-auth-choice-arrow" size={20} />
            </button>
          )}

          {/* Email Option */}
          {hasEmail && (
            <button
              type="button"
              className="lp-auth-choice"
              onClick={onRequestEmailCode}
              disabled={loading}
            >
              <div className="lp-auth-choice-icon email">
                <FiMail size={24} />
              </div>
              <div className="lp-auth-choice-content">
                <div className="lp-auth-choice-title">Email Code</div>
                <div className="lp-auth-choice-desc">We'll send an 8-digit code to your email</div>
              </div>
              <FiArrowRight className="lp-auth-choice-arrow" size={20} />
            </button>
          )}

          {/* If only email is available (no passkey), show send button */}
          {!hasBiometric && hasEmail && (
            <div style={{ textAlign: 'center', marginTop: '8px' }}>
              <p className="lp-modal-description" style={{ marginBottom: '16px' }}>
                Click above to receive a verification code via email.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Two-Factor Email Code Modal Component
 *
 * Handles the email code entry form for 2FA.
 */
const TwoFactorEmailCodeModal = ({
  loading,
  onSubmit,
  onResendCode,
  onBack
}) => {
  return (
    <div className="lp-modal-overlay active">
      <div className="lp-modal">
        <div className="lp-modal-header">
          <button
            type="button"
            className="lp-modal-back"
            onClick={onBack}
            disabled={loading}
          >
            <FiArrowLeft size={18} />
          </button>
          <h2 className="lp-modal-title">Enter Verification Code</h2>
        </div>
        <p className="lp-modal-description">
          Enter the 8-digit code we sent to your email address.
        </p>

        <form onSubmit={onSubmit}>
          <div className="lp-form-group" style={{ marginBottom: '24px' }}>
            <div className="lp-input-wrapper">
              <FiShield className="lp-input-icon" />
              <input
                type="text"
                id="code"
                name="code"
                placeholder="00000000"
                className="lp-form-input lp-code-input"
                autoComplete="off"
                maxLength="8"
                disabled={loading}
                style={{ textAlign: 'center', letterSpacing: '4px', fontSize: '20px', fontWeight: '600' }}
              />
            </div>
          </div>

          <button
            type="submit"
            className="lp-btn lp-btn-primary"
            disabled={loading}
          >
            {loading ? 'Verifying...' : 'Verify Code'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button
              type="button"
              className="lp-btn lp-btn-ghost"
              onClick={onResendCode}
              disabled={loading}
            >
              Resend Code
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Two-Factor Form Component
 *
 * Main component that renders the appropriate 2FA modal based on state.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.show2FAModal - Whether to show the 2FA modal
 * @param {boolean} props.emailCodeRequested - Whether email code has been requested
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.hasBiometric - Whether user has biometric/passkey set up
 * @param {boolean} props.hasEmail - Whether user has email 2FA available
 * @param {Function} props.onBiometric2FA - Handle biometric 2FA
 * @param {Function} props.onRequestEmailCode - Handle email code request
 * @param {Function} props.on2FASubmit - Handle 2FA code submission
 * @param {Function} props.onBack - Handle going back
 * @param {Function} props.onClose - Handle closing the modal
 * @returns {JSX.Element|null} Two-factor form component or null
 */
const TwoFactorForm = ({
  show2FAModal,
  emailCodeRequested,
  loading,
  hasBiometric,
  hasEmail,
  onBiometric2FA,
  onRequestEmailCode,
  on2FASubmit,
  onBack,
  onClose
}) => {
  if (!show2FAModal) {
    return null;
  }

  // Show email code entry form
  if (emailCodeRequested) {
    return (
      <TwoFactorEmailCodeModal
        loading={loading}
        onSubmit={on2FASubmit}
        onResendCode={onRequestEmailCode}
        onBack={onBack}
      />
    );
  }

  // Show 2FA choice modal
  return (
    <TwoFactorChoiceModal
      loading={loading}
      hasBiometric={hasBiometric}
      hasEmail={hasEmail}
      onBiometric2FA={onBiometric2FA}
      onRequestEmailCode={onRequestEmailCode}
      onClose={onClose}
    />
  );
};

export default TwoFactorForm;
